import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import React from "react";
import { Button } from "./ui/Button";

import { Modal } from "./Modal";

interface ConfirmDeleteModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** What is about to be deleted, e.g. the row's name or id. */
  description?: string;
  title?: string;
  /** Overrides the default "this deletes a database row" warning. */
  body?: React.ReactNode;
  confirmLabel?: string;
  /** Bulk writes in flight: disable confirm and show progress. */
  applying?: boolean;
  progress?: string;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  open,
  onCancel,
  onConfirm,
  description,
  title = "Delete this record?",
  body,
  confirmLabel = "Delete record",
  applying = false,
  progress,
}) => {
  const handleCancel = () => {
    if (applying) return;
    onCancel();
  };
  const handleConfirm = () => {
    if (applying) return;
    onConfirm();
  };

  return (
    <Modal
      open={open}
      title={title}
      onClose={handleCancel}
      maxWidth="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleCancel}
            disabled={applying}
            variant="secondary" size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={applying}
            variant="danger-solid" size="sm"
          >
            {applying && progress ? progress : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <WarningAmberIcon sx={{ color: "var(--od-danger)", fontSize: 20 }} />
        <div className="text-sm text-od-text">
          <p className="m-0">
            {body ?? (
              <>
                This deletes the record from your database. It cannot be undone from here.
              </>
            )}
          </p>
          {description && (
            <p className="m-0 mt-2 truncate rounded-md border border-od-border bg-od-surface-2 px-2 py-1 font-mono text-xs text-od-muted">
              {description}
            </p>
          )}
          {applying && progress && (
            <p className="m-0 mt-2 text-xs text-od-muted">{progress}</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteModal;
